import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PRODUCT_CATALOG } from "@/constants/products";
import {
  Search,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Utensils,
  PackageCheck,
  CreditCard,
  Banknote,
  Clock,
} from "lucide-react";
import type { ProductDefinition } from "@/types/orders";

interface CartItem {
  product: ProductDefinition;
  quantity: number;
}

interface ManualOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  currentStaffName: string;
  onOrderCreated: () => void;
  token: string;
}

type ServiceType = "mesa" | "llevar";
type PaymentOption = "efectivo" | "tarjeta" | "pendiente";

// Categories to show (excluding Encargos since those go through separate flow)
const ALLOWED_CATEGORIES = [
  "Bebidas Frías",
  "Bebidas Calientes",
  "Salado",
  "Dulce",
  "Menús",
  "Menús Especiales",
];

export default function ManualOrderModal({
  isOpen,
  onClose,
  locationId,
  currentStaffName,
  onOrderCreated,
  token,
}: ManualOrderModalProps) {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [serviceType, setServiceType] = useState<ServiceType>("mesa");
  const [tableNumber, setTableNumber] = useState<string>("");
  const [paymentOption, setPaymentOption] = useState<PaymentOption>("pendiente");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unavailableProducts, setUnavailableProducts] = useState<Set<string>>(new Set());

  // Fetch unavailable products for this location
  useEffect(() => {
    if (!isOpen || !locationId) return;

    const fetchAvailability = async () => {
      try {
        const { data, error } = await supabase
          .from("product_availability")
          .select("product_id")
          .eq("location_id", locationId)
          .eq("is_available", false);

        if (error) throw error;

        const unavailable = new Set(data?.map((item) => item.product_id) || []);
        setUnavailableProducts(unavailable);
      } catch (err) {
        console.error("Error fetching availability:", err);
      }
    };

    fetchAvailability();
  }, [isOpen, locationId]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCart([]);
      setSearchQuery("");
      setServiceType("mesa");
      setTableNumber("");
      setPaymentOption("pendiente");
    }
  }, [isOpen]);

  // Auto-switch payment option when changing to "llevar" with "pendiente" selected
  useEffect(() => {
    if (serviceType === "llevar" && paymentOption === "pendiente") {
      setPaymentOption("efectivo");
    }
  }, [serviceType, paymentOption]);

  // Filter products by category and search
  const filteredProducts = useMemo(() => {
    return PRODUCT_CATALOG.filter((product) => {
      const inAllowedCategory = ALLOWED_CATEGORIES.includes(product.category);
      const matchesSearch =
        searchQuery === "" ||
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase());
      const isAvailable = !unavailableProducts.has(product.id);
      return inAllowedCategory && matchesSearch && isAvailable;
    });
  }, [searchQuery, unavailableProducts]);

  // Group products by category
  const productsByCategory = useMemo(() => {
    const grouped: Record<string, ProductDefinition[]> = {};
    filteredProducts.forEach((product) => {
      if (!grouped[product.category]) {
        grouped[product.category] = [];
      }
      grouped[product.category].push(product);
    });
    return grouped;
  }, [filteredProducts]);

  // Cart calculations
  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  }, [cart]);

  const cartItemCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  // Cart operations
  const addToCart = (product: ProductDefinition) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, delta: number) => {
    setCart((prev) => {
      return prev
        .map((item) => {
          if (item.product.id === productId) {
            const newQuantity = item.quantity + delta;
            return newQuantity <= 0 ? null : { ...item, quantity: newQuantity };
          }
          return item;
        })
        .filter((item): item is CartItem => item !== null);
    });
  };

  // Submit order
  const handleSubmit = async () => {
    if (cart.length === 0) {
      toast({
        title: "Carrito vacío",
        description: "Añade productos al pedido",
        variant: "destructive",
      });
      return;
    }

    if (serviceType === "mesa" && !tableNumber) {
      toast({
        title: "Número de mesa requerido",
        description: "Introduce el número de mesa",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare payment status based on selection
      const paymentStatus =
        paymentOption === "pendiente" ? "pendiente" : "pagado";

      // Call Edge Function to create order (bypasses RLS)
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "create-order",
          token,
          orderData: {
            order_type: serviceType,
            status: "pendiente",
            payment_status: paymentStatus,
            location_id: locationId,
            table_number: serviceType === "mesa" ? parseInt(tableNumber) : null,
            served_by: currentStaffName,
          },
          items: cart.map((item) => ({
            product_name: item.product.name,
            product_category: item.product.category,
            unit_price: item.product.price,
            quantity: item.quantity,
          })),
        },
      });

      if (error) throw error;
      
      if (!data.success) {
        throw new Error(data.error || "Error al crear pedido");
      }

      toast({
        title: `✅ Pedido #${data.order.order_number} creado`,
        description: paymentStatus === "pagado" 
          ? "Cobrado correctamente" 
          : "Pendiente de pago",
      });

      onOrderCreated();
      onClose();
    } catch (error: any) {
      console.error("Error creating order:", error);
      toast({
        title: "Error al crear pedido",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShoppingCart className="h-6 w-6 text-blue-600" />
            Nuevo Pedido Manual (TPV)
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Column - Product Catalog */}
          <div className="flex-1 flex flex-col border-r">
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar productos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Products Grid */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-6">
                {Object.entries(productsByCategory).map(([category, products]) => (
                  <div key={category}>
                    <h3 className="font-semibold text-sm text-muted-foreground mb-3">
                      {category}
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                      {products.map((product) => (
                        <button
                          key={product.id}
                          onClick={() => addToCart(product)}
                          className="p-3 text-left rounded-lg border bg-card hover:bg-accent transition-colors"
                        >
                          <p className="font-medium text-sm line-clamp-2">
                            {product.name}
                          </p>
                          <p className="text-primary font-bold mt-1">
                            {product.price.toFixed(2)}€
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {filteredProducts.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    No se encontraron productos
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Right Column - Cart & Order Details */}
          <div className="w-96 flex flex-col bg-muted/30">
            {/* Service Type Selector */}
            <div className="p-4 border-b bg-background">
              <Label className="text-sm font-medium mb-3 block">
                Tipo de Servicio
              </Label>
              <RadioGroup
                value={serviceType}
                onValueChange={(v) => setServiceType(v as ServiceType)}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="mesa" id="mesa" />
                  <Label htmlFor="mesa" className="flex items-center gap-1 cursor-pointer">
                    <Utensils className="h-4 w-4" />
                    Mesa
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="llevar" id="llevar" />
                  <Label htmlFor="llevar" className="flex items-center gap-1 cursor-pointer">
                    <PackageCheck className="h-4 w-4" />
                    Para Llevar
                  </Label>
                </div>
              </RadioGroup>

              {/* Table Number (only for Mesa) */}
              {serviceType === "mesa" && (
                <div className="mt-3">
                  <Label className="text-sm">Número de Mesa</Label>
                  <Input
                    type="number"
                    min="1"
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    placeholder="Ej: 5"
                    className="mt-1 w-24"
                  />
                </div>
              )}
            </div>

            {/* Cart Items */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-2">
                {cart.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Carrito vacío</p>
                    <p className="text-sm">Pulsa en un producto para añadirlo</p>
                  </div>
                ) : (
                  cart.map((item) => (
                    <Card key={item.product.id} className="bg-background">
                      <CardContent className="p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">
                              {item.product.name}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {item.product.price.toFixed(2)}€ × {item.quantity}
                            </p>
                          </div>
                          <p className="font-bold text-primary">
                            {(item.product.price * item.quantity).toFixed(2)}€
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-8 w-8"
                            onClick={() => removeFromCart(item.product.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                          <div className="flex items-center gap-2">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product.id, -1)}
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="w-8 text-center font-bold">
                              {item.quantity}
                            </span>
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.product.id, 1)}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Payment Method */}
            <div className="p-4 border-t bg-background">
              <Label className="text-sm font-medium mb-3 block">
                Método de Pago
              </Label>
              <RadioGroup
                value={paymentOption}
                onValueChange={(v) => setPaymentOption(v as PaymentOption)}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="efectivo" id="efectivo" />
                  <Label htmlFor="efectivo" className="flex items-center gap-2 cursor-pointer">
                    <Banknote className="h-4 w-4 text-green-600" />
                    Efectivo (cobrado)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="tarjeta" id="tarjeta" />
                  <Label htmlFor="tarjeta" className="flex items-center gap-2 cursor-pointer">
                    <CreditCard className="h-4 w-4 text-blue-600" />
                    Tarjeta (datáfono)
                  </Label>
                </div>
                {/* Pendiente only available for Mesa */}
                {serviceType === "mesa" && (
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="pendiente" id="pendiente" />
                    <Label htmlFor="pendiente" className="flex items-center gap-2 cursor-pointer">
                      <Clock className="h-4 w-4 text-orange-500" />
                      Dejar pendiente de pago
                    </Label>
                  </div>
                )}
              </RadioGroup>
            </div>

            {/* Total & Submit */}
            <div className="p-4 border-t bg-primary/5">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium">Total</span>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{cartItemCount} items</Badge>
                  <span className="text-2xl font-bold text-primary">
                    {cartTotal.toFixed(2)}€
                  </span>
                </div>
              </div>
              <Button
                className="w-full h-12 text-lg font-bold bg-blue-600 hover:bg-blue-700"
                onClick={handleSubmit}
                disabled={isSubmitting || cart.length === 0}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span>
                    Creando pedido...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5" />
                    Crear Pedido
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
